import { useContext, useEffect, useState, useRef, ReactNode, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { useCompanyMemberships } from '@/hooks/useCompanyMemberships';
import { debugInfo, logError } from '@/lib/debug';
import { toast } from '@/hooks/use-toast';
import { SharedLocationContext } from './SharedLocationContext';
import {
  resolveAvailableLocations,
  resolveCurrentLocationId,
  hasSelectableMembership,
  resolveIsAllLocations,
  locationWarmScope,
  hasLocationDataForCompany,
  shouldClearStaleLocation,
} from '@/lib/location-utils';
import { readWarm, writeWarm } from '@/lib/warm-cache';

interface Location {
  id: string;
  name: string;
  code: string;
  email?: string;
  address?: string;
  phone?: string;
  timezone?: string;
  is_active: boolean;
  company_id: string;
  /** Per-location DashAlign time-clock switch (default false). Gates the
   *  owned-clock surface for lead_manager on desktop web, mirroring the
   *  kiosk PIN gate. See migration 20260707900000. */
  kiosk_clock_enabled?: boolean;
}

interface UserLocation {
  location_id: string;
  is_primary: boolean;
  role: string;
  location: Location;
}

interface LocationContextType {
  currentLocation: Location | null;
  availableLocations: Location[];
  switchLocation: (locationId: string) => Promise<void>;
  isLoadingLocation: boolean;
  canSwitchLocations: boolean;
  /** Franchisor "All locations" view — see SharedLocationContext. */
  isAllLocations: boolean;
  setAllLocationsView: (on: boolean) => void;
  refreshLocations: () => Promise<void>;
}

// Export the shared hook
export { useLocation } from './SharedLocationContext';

interface LocationProviderProps {
  children: ReactNode;
}

const LOCATION_STORAGE_KEY = 'selected_location_id';
// Franchisor "All locations" view mode — 'all' | 'store'. Absent means
// "no choice made yet": fleet-capable roles then default to 'all'.
const VIEW_MODE_KEY = 'location_view_mode';

export const LocationProvider = ({ children }: LocationProviderProps) => {
  const { profile, user, loading: authLoading, loginMethod } = useAuth();
  const [currentLocationRaw, setCurrentLocationRaw] = useState<Location | null>(null);
  const [availableLocationsRaw, setAvailableLocationsRaw] = useState<Location[]>([]);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [allLocationsRaw, setAllLocationsRaw] = useState<boolean>(() => {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) !== 'store';
    } catch {
      return true;
    }
  });
  // Suppresses the "Failed to load locations" toast during the post-login
  // race window where signOut({scope:'local'})→signInWithPassword in Auth.tsx
  // can let a stale query fire before the fresh session is established.
  // Toggles to true after the first successful fetch; subsequent failures
  // (real connectivity drops mid-session) still surface a toast.
  const hasLoadedLocationsOnceRef = useRef(false);
  // PHASE 1: Stabilize currentLocation to prevent re-renders
  const currentLocation = useMemo(() => currentLocationRaw, [
    currentLocationRaw?.id,
    currentLocationRaw?.name,
    currentLocationRaw?.code,
    currentLocationRaw?.company_id,
    currentLocationRaw?.is_active,
    currentLocationRaw?.kiosk_clock_enabled
  ]);

  // PHASE 1: Stabilize availableLocations to prevent re-renders
  const availableLocations = useMemo(() => availableLocationsRaw, [
    JSON.stringify(availableLocationsRaw.map(l => ({ id: l.id, name: l.name, code: l.code, company_id: l.company_id })))
  ]);

  // Get active profile (either auth profile or PIN session profile)
  // CRITICAL: Memoize based on primitive values, not function references
  const activeProfile = useMemo(() => {
    if (loginMethod === 'pin') {
      const pinSession = localStorage.getItem('pin_session');
      
      if (pinSession) {
        try {
          const sessionData = JSON.parse(pinSession);
          
          if (sessionData.profile && sessionData.profile.id) {
            return sessionData.profile;
          }
        } catch (error) {
          logError('LocationContext', error, { context: 'PIN session parsing' });
          localStorage.removeItem('pin_session');
        }
      }
    }
    
    return profile;
  }, [profile?.id, profile?.company_id, profile?.role, profile?.location_id, loginMethod]); // Depend on primitive values only

  // Determine if user can switch locations (super_admin/admin/corporate only - managers/employees see only their assigned location)
  const canSwitchLocations = activeProfile?.role === 'super_admin' || activeProfile?.role === 'admin' || activeProfile?.role === 'corporate';

  // Franchisor "All locations" mode — fleet-command roles only (admin /
  // super_admin / corporate, per resolveIsAllLocations/showsFleetCommand —
  // PR #725 added corporate to fleetCommand and the LocationSwitcher already
  // offers it an "All locations" entry, but this flag was never updated to
  // match, so picking it silently did nothing for corporate: isAllLocations
  // stayed false and every location-scoped surface (including the team
  // communication composer) kept using their single underlying
  // currentLocation, ticket #1190). The flag is presentation-level:
  // currentLocation stays set underneath so location-scoped surfaces never
  // see null. Defaults ON for fleet roles until the user explicitly picks a
  // store.
  const isAllLocations = resolveIsAllLocations(activeProfile?.role, allLocationsRaw);
  const setAllLocationsView = useCallback((on: boolean) => {
    setAllLocationsRaw(on);
    try {
      localStorage.setItem(VIEW_MODE_KEY, on ? 'all' : 'store');
    } catch { /* private mode */ }
  }, []);

  // Phase A.1 PR 3a — prefer the user's primary membership's company_id
  // when reachable, fall back to the scalar profile.company_id so PIN
  // sessions (no auth membership) and users whose membership row hasn't
  // synced yet keep working identically. For every existing user today
  // both sources resolve to the same value because PR #233's backfill
  // and PR #235's trigger keep them in lock-step. LocationContext is
  // the first consumer; PR 3b will migrate RLS table-by-table; PR 3c
  // will surface a company switcher UI when anyone has >1 membership.
  const { resolvedCompanyId } = useCompanyMemberships();
  const effectiveCompanyId = resolvedCompanyId ?? activeProfile?.company_id ?? null;

  // Clear location data on logout (only after auth has finished loading)
  useEffect(() => {
    if (!authLoading && !user && !profile) {
      localStorage.removeItem(LOCATION_STORAGE_KEY);
      setCurrentLocationRaw(null);
      setAvailableLocationsRaw([]);
      hasLoadedLocationsOnceRef.current = false;
    }
  }, [user, profile, authLoading]);

  // Load user locations based on role
  const loadUserLocations = useCallback(async (): Promise<UserLocation[]> => {
    if (!activeProfile?.id) {
      debugInfo('LocationContext', 'No active profile available for location loading');
      return [];
    }

    // CRITICAL: Skip all direct database queries for PIN sessions
    if (loginMethod === 'pin') {
      debugInfo('LocationContext', 'PIN session detected - skipping user_locations query');
      
      // For PIN sessions, construct location from session data
      const pinSession = localStorage.getItem('pin_session');
      if (pinSession) {
        try {
          const sessionData = JSON.parse(pinSession);
          if (sessionData.location && sessionData.location_id) {
            return [{
              location_id: sessionData.location_id,
              is_primary: true,
              role: sessionData.profile?.role || 'employee',
              location: {
                id: sessionData.location.id,
                name: sessionData.location.name,
                code: sessionData.location.code,
                is_active: true,
                company_id: sessionData.profile?.company_id || sessionData.company?.id
              }
            }] as UserLocation[];
          }
        } catch (error) {
          logError('LocationContext', error, { context: 'PIN session location parsing' });
        }
      }
      return [];
    }

    // Defer until the active company is known, mirroring
    // loadAllCompanyLocations. An unscoped query here would be the
    // cross-tenant leak this function exists to prevent.
    if (!effectiveCompanyId) {
      debugInfo('LocationContext', 'No effective company id yet; deferring user location load');
      return [];
    }

    try {
      debugInfo('LocationContext', 'Loading locations for user', { 
        profileId: activeProfile.id, 
        role: activeProfile.role,
        companyId: effectiveCompanyId
      });

      // Use regular query for authenticated sessions
      const { data, error } = await supabase
        .from('user_locations')
        .select(`
          location_id,
          is_primary,
          role,
          locations(
            id,
            name,
            code,
            email,
            address,
            phone,
            timezone,
            is_active,
            company_id,
            kiosk_clock_enabled
          )
        `)
        .eq('user_id', activeProfile.id)
        .eq('locations.is_active', true)
        // Scope to the ACTIVE company. user_locations is keyed on the person,
        // not the tenant, so a multi-company account (the brian+ops support
        // login, which carries a membership in every customer tenant) was
        // getting whatever rows it happened to have — the JIUFD demo's
        // "Northgate" showed up in every location picker while working inside
        // a customer's company, including the admin user editor. company_id is
        // already selected on the join; it just was never filtered on.
        //
        // This MUST be effectiveCompanyId, not activeProfile.company_id: the
        // active company is resolved asynchronously (AuthContext rewrites the
        // exposed profile.company_id only once memberships load), and this
        // callback's dependency list is what decides when the closure is
        // rebuilt. Reading the scalar here while omitting it from the deps
        // pinned the query to whatever company was active on FIRST render —
        // the ops account's HOME company — for the rest of the session, which
        // is exactly how Northgate kept coming back. See the deps below.
        .eq('locations.company_id', effectiveCompanyId)
        .order('is_primary', { ascending: false });

      if (error) {
        logError('LocationContext', error, { profileId: activeProfile.id });
        throw error;
      }

      debugInfo('LocationContext', 'User locations loaded', {
        count: data?.length || 0,
        locations: data?.map(ul => ul.locations?.name)
      });

      hasLoadedLocationsOnceRef.current = true;

      // Map the data to match UserLocation interface
      const mappedData = (data || []).map(item => ({
        location_id: item.location_id,
        is_primary: item.is_primary,
        role: item.role,
        location: item.locations
      }));

      // Company-wide roles fall back to every active store in the active
      // company when they have no user_locations rows there. Those rows are
      // per-person bookkeeping; an admin's authority is company-wide, so
      // nobody should have to remember to create them before an admin can see
      // their own stores. Without this, scoping the query above to the active
      // company would leave a support/multi-tenant admin with an EMPTY
      // location picker instead of the wrong one.
      const isCompanyWideRole =
        activeProfile.role === 'admin' ||
        activeProfile.role === 'super_admin' ||
        activeProfile.role === 'corporate';

      // Gated on a SELECTABLE store, not on row count: a membership whose
      // embedded location was filtered out (inactive store, other tenant)
      // comes back as a row with a NULL embed, which is just as empty a
      // picker as no rows at all. See hasSelectableMembership.
      if (!hasSelectableMembership(mappedData)) {
        if (isCompanyWideRole && effectiveCompanyId) {
          const { data: companyLocations, error: companyErr } = await supabase
            .from('locations')
            .select('id, name, code, email, address, phone, timezone, is_active, company_id, kiosk_clock_enabled')
            .eq('company_id', effectiveCompanyId)
            .eq('is_active', true)
            .order('name');

          if (!companyErr && companyLocations?.length) {
            return companyLocations.map((loc, idx) => ({
              location_id: loc.id,
              is_primary: idx === 0,
              role: activeProfile.role,
              location: loc,
            })) as UserLocation[];
          }
        }

        // #1701: the home store on the profile is the OTHER half of the
        // membership union every roster surface already resolves -- home
        // store OR user_locations (src/lib/location-membership.ts,
        // unionRosterProfileIds). The picker is the one surface that was
        // never converted, and it is the surface everything else hangs off:
        // with no selectable location, currentLocation stays null and every
        // location-scoped query in the app is `enabled: !!locationId`, so an
        // associate sees a blank schedule rather than an error. A manager
        // meanwhile sees them on the store roster and schedules them, because
        // that side of the union does read profiles.location_id.
        //
        // The gap is real and routine: create-user writes the primary
        // user_locations row best-effort (it logs and carries on if the
        // insert fails), the edit dialog only syncs memberships when its
        // location field is filled in, and bulk imports set the home store.
        // Scoped to the active company and to an active store, so this can
        // never widen what a person can see -- and it cannot widen what they
        // can READ either: the server still answers every query under RLS.
        if (activeProfile.location_id && effectiveCompanyId) {
          const { data: homeLocation, error: homeErr } = await supabase
            .from('locations')
            .select('id, name, code, email, address, phone, timezone, is_active, company_id, kiosk_clock_enabled')
            .eq('id', activeProfile.location_id)
            .eq('company_id', effectiveCompanyId)
            .eq('is_active', true)
            .maybeSingle();

          if (!homeErr && homeLocation) {
            debugInfo('LocationContext', 'No membership row; falling back to the home store', {
              locationId: homeLocation.id,
            });
            return [{
              location_id: homeLocation.id,
              is_primary: true,
              role: activeProfile.role,
              location: homeLocation,
            }] as UserLocation[];
          }
        }
      }

      return mappedData as UserLocation[];
    } catch (error) {
      logError('LocationContext', error, { context: 'loadUserLocations' });
      if (hasLoadedLocationsOnceRef.current) {
        toast({
          title: "Error Loading Locations",
          description: "Failed to load your assigned locations. Please try again.",
          variant: "destructive",
        });
      }
      return [];
    }
  }, [activeProfile?.id, activeProfile?.role, activeProfile?.location_id, effectiveCompanyId, loginMethod]);

  // Load all locations across all companies (for admin/corporate)
  const loadAllCompanyLocations = useCallback(async (): Promise<Location[]> => {
    if (!activeProfile || !canSwitchLocations) {
      return [];
    }

    try {
      debugInfo('LocationContext', 'Loading ALL locations for admin/corporate', { 
        profileId: activeProfile.id,
        role: activeProfile.role,
        loginMethod
      });

      // For PIN sessions, skip direct database query to avoid 401 errors
      // PIN sessions should only use pre-loaded location data
      if (loginMethod === 'pin') {
        debugInfo('LocationContext', 'PIN session detected - skipping direct location query');
        return [];
      }

      // Admin/Corporate can see ALL active locations for their company.
      // effectiveCompanyId resolves to the primary membership's company
      // when available, falling back to activeProfile.company_id —
      // identical value today since every existing user has exactly
      // one membership matching their profile.company_id.
      if (!effectiveCompanyId) {
        debugInfo('LocationContext', 'No effective company id yet; deferring location load');
        return [];
      }
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', effectiveCompanyId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        logError('LocationContext', error, { profileId: activeProfile.id });
        throw error;
      }

      debugInfo('LocationContext', 'All locations loaded for admin/corporate', {
        count: data?.length || 0,
        locationNames: data?.map(l => l.name)
      });

      hasLoadedLocationsOnceRef.current = true;

      return (data || []) as Location[];
    } catch (error) {
      logError('LocationContext', error, { context: 'loadAllCompanyLocations' });
      if (hasLoadedLocationsOnceRef.current) {
        toast({
          title: "Error Loading Locations",
          description: "Failed to load accessible locations. Please try again.",
          variant: "destructive",
        });
      }
      return [];
    }
  }, [activeProfile?.id, activeProfile?.role, effectiveCompanyId, canSwitchLocations, loginMethod]);

  // Refresh locations data - optimized for PIN sessions
  // opts.silent: revalidate WITHOUT toggling isLoadingLocation. Used after a
  // warm-cache hydrate has already painted last-known locations and cleared the
  // spinner, so the background refresh doesn't flash the skeleton back on.
  const refreshLocations = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!activeProfile?.id || authLoading) {
      console.log('[LOCATION_CONTEXT_DEBUG] ⏸️ Skipping refresh - no profile or auth loading', {
        hasProfile: !!activeProfile?.id,
        authLoading
      });
      return;
    }

    // CRITICAL: For PIN sessions on limited routes, use PIN session data only (no DB calls)
    if (loginMethod === 'pin' && window.location.pathname.includes('/limited')) {
      console.log('[LOCATION_CONTEXT_DEBUG] 🔒 PIN session on limited route - using storage only');
      const pinSession = localStorage.getItem('pin_session');
      if (pinSession) {
        try {
          const sessionData = JSON.parse(pinSession);
          if (sessionData.location) {
            const location = sessionData.location;
            setAvailableLocationsRaw([location]);
            setCurrentLocationRaw(location);
            setIsLoadingLocation(false);
            console.log('[LOCATION_CONTEXT_DEBUG] ✅ Set location from PIN session', { locationName: location.name });
            return;
          }
        } catch (error) {
          logError('LocationContext', error, { context: 'PIN session location parse' });
        }
      }
    }

    const isInitialLoad = !currentLocationRaw?.id;
    console.log('[LOCATION_CONTEXT_DEBUG] 🔄 Starting location refresh', {
      currentLocationBefore: currentLocationRaw?.id,
      currentLocationName: currentLocationRaw?.name,
      isInitialLoad
    });

    if (isInitialLoad && !silent) {
      setIsLoadingLocation(true);
    }

    try {
      // Load the user's assigned locations and (for fleet roles) the full
      // company location list in PARALLEL. These two queries are independent
      // — loadAllCompanyLocations keys off effectiveCompanyId, not off the
      // user_locations result — so awaiting them sequentially just stacked two
      // round-trips behind the dashboard skeleton on every cold launch. On a
      // high-latency mobile connection that was a visible pause for
      // admin/corporate users. loadAllCompanyLocations already self-guards
      // (returns [] when !canSwitchLocations / no company id), but we skip the
      // call entirely for non-fleet users to avoid a needless request.
      const [userLocations, allCompanyLocations] = await Promise.all([
        loadUserLocations(),
        canSwitchLocations ? loadAllCompanyLocations() : Promise.resolve([] as Location[]),
      ]);

      if (canSwitchLocations && allCompanyLocations.length === 0) {
        debugInfo('LocationContext', 'Admin locations query empty, using user locations');
      }

      const { locations, primaryLocationId } = resolveAvailableLocations(
        userLocations,
        allCompanyLocations,
        canSwitchLocations,
      ) as { locations: Location[]; primaryLocationId: string | null };

      const existingIds = availableLocationsRaw.map(l => l.id).sort().join(',');
      const newIds = locations.map(l => l.id).sort().join(',');
      if (existingIds !== newIds) {
        setAvailableLocationsRaw(locations);
      }

      // Determine current location with priority: PIN session > saved > primary > first
      let targetLocationId: string | null = null;
      
      // Priority 1: For PIN sessions, use the location_id from PIN session data
      if (loginMethod === 'pin') {
        const pinSession = localStorage.getItem('pin_session');
        if (pinSession) {
          try {
            const sessionData = JSON.parse(pinSession);
            if (sessionData.location_id) {
              const pinLocationExists = locations.some(loc => loc.id === sessionData.location_id);
              if (pinLocationExists) {
                targetLocationId = sessionData.location_id;
                debugInfo('LocationContext', 'Using PIN session location', {
                  locationId: targetLocationId,
                  loginMethod
                });
              }
            }
          } catch (error) {
            logError('LocationContext', error, { context: 'Reading PIN session location' });
          }
        }
      }
      
      // Priority 2: Use saved location from localStorage (if not PIN session)
      if (!targetLocationId) {
        const savedLocationId = localStorage.getItem(LOCATION_STORAGE_KEY);
        if (savedLocationId) {
          const locationExists = locations.some(loc => loc.id === savedLocationId);
          if (locationExists) {
            targetLocationId = savedLocationId;
            debugInfo('LocationContext', 'Using saved location', savedLocationId);
          } else {
            debugInfo('LocationContext', 'Saved location no longer accessible', savedLocationId);
            localStorage.removeItem(LOCATION_STORAGE_KEY);
          }
        }
      }

      // Priority 3: Fall back to primary location or first available.
      // resolveCurrentLocationId re-checks every candidate against the
      // selectable list, so a primary that belongs to another tenant can no
      // longer resolve to a target this function then fails to find — the
      // path that left the previous company's store showing in the picker.
      if (!targetLocationId) {
        targetLocationId = resolveCurrentLocationId(locations, [primaryLocationId]);
        debugInfo('LocationContext', 'Using fallback location (primary or first)', targetLocationId);
      }

      // PHASE 2 FIX: Set current location - NEVER set to null during updates, preserve previous value
      if (targetLocationId) {
        const selectedLocation = locations.find(loc => loc.id === targetLocationId);
        
        if (selectedLocation) {
          if (currentLocationRaw?.id === selectedLocation.id) {
            console.log('[LOCATION_CONTEXT_DEBUG] ⏭️ Same location, skipping state update', {
              locationId: selectedLocation.id,
              locationName: selectedLocation.name
            });
          } else {
            console.log('[LOCATION_CONTEXT_DEBUG] ✅ Setting new location', {
              oldLocation: currentLocationRaw?.id,
              newLocation: selectedLocation.id,
              newLocationName: selectedLocation.name
            });
            setCurrentLocationRaw(selectedLocation);
          }
          localStorage.setItem(LOCATION_STORAGE_KEY, targetLocationId);
          debugInfo('LocationContext', 'Current location set', { 
            locationName: selectedLocation.name,
            locationId: selectedLocation.id 
          });
        } else {
          console.warn('[LOCATION_CONTEXT_DEBUG] ⚠️ Target location not found in available locations', {
            targetLocationId,
            availableLocationIds: locations.map(l => l.id)
          });
        }
        // Whatever happened above, never leave another company's store on
        // screen: the label in the LocationSwitcher renders currentLocation
        // directly, with no cross-check against availableLocations.
        if (!selectedLocation && shouldClearStaleLocation(currentLocationRaw, effectiveCompanyId)) {
          debugInfo('LocationContext', 'Dropping location from a different company', {
            staleLocationId: currentLocationRaw?.id,
            effectiveCompanyId,
          });
          setCurrentLocationRaw(null);
        }
      } else {
        // CRITICAL: Only set to null if we're certain there are no locations
        // AND we're not in the middle of a refresh
        console.warn('[LOCATION_CONTEXT_DEBUG] ⚠️ No target location ID - preserving current location', {
          currentLocation: currentLocationRaw?.id,
          availableLocations: locations.length
        });
        
        // Only clear if we have no locations at all and no current location —
        // or if the one we are holding belongs to a company we are no longer
        // scoped to, which is never worth preserving.
        if (locations.length === 0 && !currentLocationRaw) {
          debugInfo('LocationContext', 'No locations available for user - clearing');
          setCurrentLocationRaw(null);
        } else if (shouldClearStaleLocation(currentLocationRaw, effectiveCompanyId)) {
          debugInfo('LocationContext', 'No target and current location is another company\'s - clearing', {
            staleLocationId: currentLocationRaw?.id,
            effectiveCompanyId,
          });
          setCurrentLocationRaw(null);
        }
        // Otherwise keep the current location to prevent form resets
      }

      // Warm-start write-through (email sessions only — PIN sessions
      // reconstruct their single location synchronously from the session, so
      // there's nothing to warm). Cache the resolved list + selection keyed by
      // the user's profile id so the next launch paints instantly. currentId
      // falls back to the preserved current location when no new target
      // resolved this pass.
      if (loginMethod !== 'pin' && locations.length > 0) {
        // Company-scoped key — see locationWarmScope. Writing under a
        // profile-only key would let the NEXT company's launch read this
        // company's stores.
        writeWarm('location', locationWarmScope(activeProfile.id, effectiveCompanyId), {
          available: locations,
          // Only cache a selection that exists in the cached list. Writing a
          // target that failed to resolve would hand the next launch a
          // currentId the warm read cannot find — a silent cache miss at best,
          // and the same foreign-store display if anything ever trusted it.
          currentId: resolveCurrentLocationId(locations, [targetLocationId, currentLocationRaw?.id]),
        });
      }

    } catch (error) {
      logError('LocationContext', error, { context: 'refreshLocations' });
    } finally {
      console.log('[LOCATION_CONTEXT_DEBUG] 🏁 Refresh complete', {
        currentLocationAfter: currentLocationRaw?.id,
        currentLocationName: currentLocationRaw?.name,
        availableLocationsCount: availableLocationsRaw.length
      });
      if (isInitialLoad && !silent) {
        setIsLoadingLocation(false);
      }
    }
  }, [activeProfile?.id, activeProfile?.role, activeProfile?.company_id, effectiveCompanyId, authLoading, canSwitchLocations, loadAllCompanyLocations, loadUserLocations, loginMethod]);
  // NOTE: currentLocationRaw deliberately excluded to prevent infinite loop

  const refreshLocationsRef = useRef(refreshLocations);
  useEffect(() => { refreshLocationsRef.current = refreshLocations; }, [refreshLocations]);

  const stableRefreshLocations = useCallback(async () => {
    return refreshLocationsRef.current();
  }, []);

  // Switch to different location
  const switchLocation = useCallback(async (locationId: string) => {
    const targetLocation = availableLocations.find(loc => loc.id === locationId);
    
    if (!targetLocation) {
      toast({
        title: "Invalid Location",
        description: "The selected location is not available.",
        variant: "destructive",
      });
      return;
    }

    try {
      debugInfo('LocationContext', 'Switching to location', { 
        fromLocation: currentLocation?.name,
        fromLocationId: currentLocation?.id,
        fromCompanyId: currentLocation?.company_id,
        toLocation: targetLocation.name,
        toLocationId: locationId,
        toCompanyId: targetLocation.company_id,
        fullLocation: targetLocation
      });

      // Update state first. Picking a concrete store is explicit intent —
      // it always exits the franchisor "All locations" view.
      setCurrentLocationRaw(targetLocation);
      setAllLocationsRaw(false);
      localStorage.setItem(LOCATION_STORAGE_KEY, locationId);
      try {
        localStorage.setItem(VIEW_MODE_KEY, 'store');
      } catch { /* private mode */ }
      
      // Small delay to ensure state is updated before event fires
      setTimeout(() => {
        debugInfo('LocationContext', 'Location switch complete, dispatching event');
        
        // Trigger data refresh for location-dependent hooks
        window.dispatchEvent(new CustomEvent('locationChanged', { 
          detail: { 
            locationId, 
            locationName: targetLocation.name,
            companyId: targetLocation.company_id
          } 
        }));
      }, 50);

      toast({
        title: "Location Changed",
        description: `Switched to ${targetLocation.name}`,
      });

    } catch (error) {
      logError('LocationContext', error, { context: 'switchLocation', locationId });
      toast({
        title: "Error Switching Location",
        description: "Failed to switch location. Please try again.",
        variant: "destructive",
      });
    }
  }, [availableLocations, currentLocation, toast]);

  const switchLocationRef = useRef(switchLocation);
  useEffect(() => { switchLocationRef.current = switchLocation; }, [switchLocation]);

  const stableSwitchLocation = useCallback(async (locationId: string) => {
    return switchLocationRef.current(locationId);
  }, []);

  // Initialize locations when profile is available
  useEffect(() => {
    let mounted = true;
    
    // PHASE 3 FIX: CRITICAL guard - Skip initialization entirely for PIN sessions on limited routes
    if (loginMethod === 'pin' && window.location.pathname.includes('/limited')) {
      console.log('[LOCATION_CONTEXT_DEBUG] 🚫 BLOCKING LocationContext initialization for PIN session on limited route');
      setIsLoadingLocation(false);
      return;
    }
    
    // Failsafe timeout to prevent infinite loading
    const failsafeTimeout = setTimeout(() => {
      if (mounted && isLoadingLocation) {
        if (import.meta.env.DEV) {
          console.warn('[LOCATION_CONTEXT_DEBUG] ⚠️ Failsafe timeout reached after 6s - forcing load complete');
        }
        setIsLoadingLocation(false);
      }
    }, 6000); // 6 seconds to accommodate optimized auth flow
    
    if (!authLoading && activeProfile?.id) {
      // Skip only when the data we already hold belongs to the company that
      // is active NOW. Checking merely that data EXISTS strands a company
      // switch on the previous tenant's stores — see hasLocationDataForCompany.
      if (hasLocationDataForCompany(availableLocationsRaw, currentLocationRaw?.id, effectiveCompanyId)) {
        console.log('[LOCATION_CONTEXT_DEBUG] ⏭️ Skipping refresh - already have valid location data');
        setIsLoadingLocation(false);
      } else if (loginMethod !== 'pin') {
        // Warm-start: hydrate last-known locations from disk (keyed by profile
        // id) so the dashboard skeleton clears immediately, then revalidate
        // SILENTLY so the background refresh doesn't flash the skeleton back.
        // Cache miss → fall through to a normal (spinner-showing) refresh, i.e.
        // unchanged cold-start behavior.
        (async () => {
          const cached = await readWarm<{ available: Location[]; currentId: string | null }>(
            'location',
            locationWarmScope(activeProfile.id, effectiveCompanyId),
          );
          if (!mounted) return;
          if (cached?.available?.length) {
            setAvailableLocationsRaw(cached.available);
            const cur = cached.currentId
              ? cached.available.find(l => l.id === cached.currentId)
              : null;
            if (cur) setCurrentLocationRaw(cur);
            setIsLoadingLocation(false);
            refreshLocations({ silent: true });
          } else {
            refreshLocations();
          }
        })();
      } else {
        refreshLocations();
      }
    } else if (!authLoading && !activeProfile?.id) {
      // Auth finished but no profile - stop loading
      setIsLoadingLocation(false);
    }
    
    return () => {
      mounted = false;
      clearTimeout(failsafeTimeout);
    };
    // effectiveCompanyId is a dep because the company switcher resolves it
    // AFTER first paint (memberships load async): without it this effect
    // never re-runs for a switch and the stale tenant's stores persist.
  }, [authLoading, activeProfile?.id, activeProfile?.company_id, effectiveCompanyId, loginMethod]); // Don't depend on refreshLocations to prevent infinite loop

  // Handle loading state - only set to true if auth is loading, but don't get stuck
  useEffect(() => {
    if (authLoading) {
      setIsLoadingLocation(true);
    }
  }, [authLoading]);

  // PHASE 1: Memoize context value to prevent unnecessary re-renders
  const value: LocationContextType = useMemo(() => ({
    currentLocation,
    availableLocations,
    switchLocation: stableSwitchLocation,
    isLoadingLocation,
    canSwitchLocations,
    isAllLocations,
    setAllLocationsView,
    refreshLocations: stableRefreshLocations,
  }), [currentLocation, availableLocations, stableSwitchLocation, isLoadingLocation, canSwitchLocations, isAllLocations, setAllLocationsView, stableRefreshLocations]);

  return (
    <SharedLocationContext.Provider value={value}>
      {children}
    </SharedLocationContext.Provider>
  );
};